/**
 * BDSJEA-167 - Carga del tambo por establecimiento.
 *
 * Un tambo activo por establecimiento. El panel muestra el impacto económico
 * calculado por el backend junto al formulario: la idea es que quien carga vea
 * qué produce cada número antes de guardar, y no descubra el margen recién en
 * el informe.
 */
import React, { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import dayjs from "dayjs"

import {
  getEstablecimientos,
  getTamboPorEstablecimiento,
  getTambosPorProductor,
  getValoresPorDefectoTambo,
  guardarTambo,
  eliminarTambo,
} from "api/agro"
import toastHandler from "toastHandler"
import useAuthorization from "hooks/useAuthorization"
import { formatearNumero } from "helpers/formatters"

const VACIO = {
  vacasEnOrdene: "",
  vacasSecas: "",
  vaquillonasReposicion: "",
  litrosVacaDia: "",
  porcentajeTambero: "",
  porcentajeReposicion: "",
  fechaVigencia: dayjs().format("YYYY-MM-DD"),
  observaciones: "",
}

/**
 * Los tres campos que el banco parametriza en Valores Generales. Un tambo nuevo
 * arranca con esos valores en lugar de en blanco.
 *
 * Si el usuario no los toca se guardan igual, como valores propios del tambo.
 * Es deliberado: una vez cargado, el tambo describe una explotación concreta y
 * no debería cambiar de productividad porque el banco reajustó un parámetro
 * general. La leyenda del panel aclara de dónde salió cada número.
 */
const CAMPOS_PARAMETRIZADOS = [
  "litrosVacaDia",
  "porcentajeTambero",
  "porcentajeReposicion",
]

const numeroONull = v =>
  v === "" || v === null || v === undefined ? null : Number(v)

const Tambo = ({ idProductor }) => {
  const { puedeModificarObjeto } = useAuthorization()
  // BDSJEA-220: el rol consulta no puede grabar (no ve el botón Guardar), pero
  // hasta ahora los campos quedaban editables igual. El formulario debe verse
  // pero no tocarse cuando no hay permiso de modificación.
  const soloLectura = !puedeModificarObjeto("/agro/establecimientos")
  const setSuccess = toastHandler(s => s.setSuccess)
  const setError = toastHandler(s => s.setError)

  const [establecimientos, setEstablecimientos] = useState([])
  const [seleccionado, setSeleccionado] = useState("")
  const [form, setForm] = useState(VACIO)
  const [calculo, setCalculo] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [existe, setExiste] = useState(false)
  const [porDefecto, setPorDefecto] = useState(null)
  // Resumen de todos los tambos del productor. Se muestra al entrar, sin que
  // haya que elegir establecimiento: con varios, ir uno por uno para ver qué hay
  // cargado es incómodo y además no deja ver el total.
  const [resumen, setResumen] = useState([])

  useEffect(() => {
    ;(async () => {
      try {
        const [data, defaults, tambos] = await Promise.all([
          getEstablecimientos(idProductor),
          getValoresPorDefectoTambo().catch(() => null),
          getTambosPorProductor(idProductor).catch(() => []),
        ])
        setEstablecimientos(data || [])
        setPorDefecto(defaults)
        setResumen(tambos || [])
      } catch (error) {
        setError("No se pudieron cargar los establecimientos: " + error.message)
      }
    })()
  }, [idProductor, setError])

  /** Formulario en blanco pero con los parámetros del banco ya cargados. */
  const formInicial = useCallback(() => {
    if (!porDefecto) return { ...VACIO }
    const base = { ...VACIO }
    CAMPOS_PARAMETRIZADOS.forEach(campo => {
      if (porDefecto[campo] !== null && porDefecto[campo] !== undefined) {
        base[campo] = porDefecto[campo]
      }
    })
    return base
  }, [porDefecto])

  const cargarTambo = useCallback(
    async idEstablecimiento => {
      if (!idEstablecimiento) {
        setForm(formInicial())
        setCalculo(null)
        setExiste(false)
        return
      }
      setCargando(true)
      try {
        const t = await getTamboPorEstablecimiento(idEstablecimiento)
        if (t) {
          setForm({
            vacasEnOrdene: t.vacasEnOrdene ?? "",
            vacasSecas: t.vacasSecas ?? "",
            vaquillonasReposicion: t.vaquillonasReposicion ?? "",
            litrosVacaDia: t.litrosVacaDia ?? "",
            porcentajeTambero: t.porcentajeTambero ?? "",
            porcentajeReposicion: t.porcentajeReposicion ?? "",
            fechaVigencia: t.fechaVigencia || dayjs().format("YYYY-MM-DD"),
            observaciones: t.observaciones || "",
          })
          setCalculo(t)
          setExiste(true)
        } else {
          // Tambo nuevo: arranca con los parámetros del banco.
          setForm(formInicial())
          setCalculo(null)
          setExiste(false)
        }
      } catch (error) {
        setError("No se pudo cargar el tambo: " + error.message)
      } finally {
        setCargando(false)
      }
    },
    [setError, formInicial],
  )

  const handleSeleccion = id => {
    setSeleccionado(id)
    cargarTambo(id)
  }

  const handleGuardar = async e => {
    e.preventDefault()
    if (!seleccionado) return
    if (!form.vacasEnOrdene || Number(form.vacasEnOrdene) <= 0) {
      setError("Indicá cuántas vacas hay en ordeñe")
      return
    }
    setGuardando(true)
    try {
      const guardado = await guardarTambo(seleccionado, {
        vacasEnOrdene: numeroONull(form.vacasEnOrdene),
        vacasSecas: numeroONull(form.vacasSecas) || 0,
        vaquillonasReposicion: numeroONull(form.vaquillonasReposicion) || 0,
        litrosVacaDia: numeroONull(form.litrosVacaDia),
        porcentajeTambero: numeroONull(form.porcentajeTambero),
        porcentajeReposicion: numeroONull(form.porcentajeReposicion),
        fechaVigencia: form.fechaVigencia,
        observaciones: form.observaciones || null,
      })
      setCalculo(guardado)
      setExiste(true)
      setResumen(await getTambosPorProductor(idProductor).catch(() => resumen))
      setSuccess("Tambo guardado")
    } catch (error) {
      setError(
        "Error al guardar: " + (error?.response?.data?.error || error.message),
      )
    } finally {
      setGuardando(false)
    }
  }

  const handleEliminar = async () => {
    if (!window.confirm("¿Dar de baja el tambo de este establecimiento?"))
      return
    try {
      await eliminarTambo(seleccionado)
      setForm(formInicial())
      setCalculo(null)
      setExiste(false)
      setResumen(await getTambosPorProductor(idProductor).catch(() => resumen))
      setSuccess("Tambo dado de baja")
    } catch (error) {
      setError("Error al dar de baja: " + error.message)
    }
  }

  const set = campo => e => setForm({ ...form, [campo]: e.target.value })

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="mdi mdi-cow me-2 text-primary" />
          Actividad Tambo
        </h5>
      </div>

      {resumen.length > 0 && (
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">
              Tambos cargados
              <Badge color="light" className="ms-2 text-dark">
                {resumen.length}
              </Badge>
            </h6>
            <Table size="sm" responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Establecimiento</th>
                  <th className="text-end">En ordeñe</th>
                  <th className="text-end">Lts/día</th>
                  <th className="text-end">Ingreso</th>
                  <th className="text-end">Costos</th>
                  <th className="text-end">Margen</th>
                  <th>Vigente desde</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {resumen.map(t => (
                  <tr key={t.id}>
                    <td>{t.nombreEstablecimiento}</td>
                    <td className="text-end">
                      {formatearNumero(t.vacasEnOrdene, 0)}
                    </td>
                    <td className="text-end">
                      {formatearNumero(t.litrosDia, 0)}
                    </td>
                    <td className="text-end text-success">
                      {formatearNumero(t.ingresoAnualUsd, 0)}
                    </td>
                    <td className="text-end text-danger">
                      {formatearNumero(t.costoAnualUsd, 0)}
                    </td>
                    <td
                      className={`text-end fw-bold ${
                        Number(t.margenAnualUsd) < 0
                          ? "text-danger"
                          : "text-success"
                      }`}
                    >
                      {formatearNumero(t.margenAnualUsd, 0)}
                    </td>
                    <td>
                      {t.fechaVigencia
                        ? dayjs(t.fechaVigencia).format("DD/MM/YYYY")
                        : "—"}
                    </td>
                    <td className="text-end">
                      {puedeModificarObjeto("/agro/establecimientos") && (
                        <Button
                          color="link"
                          size="sm"
                          className="p-0"
                          onClick={() =>
                            handleSeleccion(String(t.idEstablecimiento))
                          }
                        >
                          Editar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {resumen.length > 1 && (
                <tfoot>
                  <tr className="border-top">
                    <th>Total</th>
                    <th className="text-end">
                      {formatearNumero(
                        resumen.reduce((a, t) => a + (t.vacasEnOrdene || 0), 0),
                        0,
                      )}
                    </th>
                    <th className="text-end">
                      {formatearNumero(
                        resumen.reduce(
                          (a, t) => a + Number(t.litrosDia || 0),
                          0,
                        ),
                        0,
                      )}
                    </th>
                    <th className="text-end text-success">
                      {formatearNumero(
                        resumen.reduce(
                          (a, t) => a + Number(t.ingresoAnualUsd || 0),
                          0,
                        ),
                        0,
                      )}
                    </th>
                    <th className="text-end text-danger">
                      {formatearNumero(
                        resumen.reduce(
                          (a, t) => a + Number(t.costoAnualUsd || 0),
                          0,
                        ),
                        0,
                      )}
                    </th>
                    <th className="text-end">
                      {formatearNumero(
                        resumen.reduce(
                          (a, t) => a + Number(t.margenAnualUsd || 0),
                          0,
                        ),
                        0,
                      )}
                    </th>
                    <th colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </Table>
            <small className="text-muted d-block mt-2">
              Importes anuales en USD. Este margen es el que entra en la
              proyección crediticia.
            </small>
          </CardBody>
        </Card>
      )}

      <FormGroup>
        <Label>
          {resumen.length > 0
            ? "Agregar o editar el tambo de un establecimiento"
            : "Establecimiento"}
        </Label>
        <Input
          type="select"
          value={seleccionado}
          onChange={e => handleSeleccion(e.target.value)}
        >
          <option value="">Seleccione...</option>
          {establecimientos.map(e => (
            <option key={e.id} value={e.id}>
              {e.nombre}
            </option>
          ))}
        </Input>
      </FormGroup>

      {!seleccionado && resumen.length === 0 && (
        <Alert color="light" className="border">
          Este productor no tiene tambos cargados. Elegí un establecimiento para
          cargar el primero. Se admite un tambo por establecimiento.
        </Alert>
      )}

      {seleccionado && cargando && (
        <div className="text-center py-4">
          <Spinner size="sm" /> Cargando...
        </div>
      )}

      {seleccionado && !cargando && (
        <Row>
          <Col lg={7}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Datos del rodeo</h6>
                  {existe ? (
                    <Badge color="success">Cargado</Badge>
                  ) : (
                    <Badge color="secondary">Sin cargar</Badge>
                  )}
                </div>

                <Form onSubmit={handleGuardar}>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Vacas en ordeñe *</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.vacasEnOrdene}
                          onChange={set("vacasEnOrdene")}
                          disabled={soloLectura}
                        />
                        <small className="text-muted">
                          Las únicas que generan ingreso.
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Vacas secas</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.vacasSecas}
                          onChange={set("vacasSecas")}
                          disabled={soloLectura}
                        />
                        <small className="text-muted">
                          Generan costo, no ingreso.
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Vaquillonas de reposición</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.vaquillonasReposicion}
                          onChange={set("vaquillonasReposicion")}
                          disabled={soloLectura}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Litros por vaca por día</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={form.litrosVacaDia}
                          onChange={set("litrosVacaDia")}
                          disabled={soloLectura}
                        />
                        <small className="text-muted">
                          {!existe && porDefecto
                            ? `Valor del banco: ${porDefecto.litrosVacaDia}. Ajustalo si medís otro.`
                            : "Productividad de este tambo."}
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>% tambero</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={form.porcentajeTambero}
                          onChange={set("porcentajeTambero")}
                          disabled={soloLectura}
                        />
                        {!existe && porDefecto && (
                          <small className="text-muted">
                            Valor del banco: {porDefecto.porcentajeTambero}%
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>% reposición anual</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={form.porcentajeReposicion}
                          onChange={set("porcentajeReposicion")}
                          disabled={soloLectura}
                        />
                        {!existe && porDefecto && (
                          <small className="text-muted">
                            Valor del banco: {porDefecto.porcentajeReposicion}%
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Dato vigente desde</Label>
                        <Input
                          type="date"
                          value={form.fechaVigencia}
                          onChange={set("fechaVigencia")}
                          disabled={soloLectura}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={8}>
                      <FormGroup>
                        <Label>Observaciones</Label>
                        <Input
                          type="text"
                          maxLength={1000}
                          value={form.observaciones}
                          onChange={set("observaciones")}
                          disabled={soloLectura}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2">
                    {puedeModificarObjeto("/agro/establecimientos") && (
                      <Button
                        color="success"
                        type="submit"
                        disabled={guardando}
                      >
                        {guardando ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="mdi mdi-content-save me-1" />
                        )}
                        Guardar
                      </Button>
                    )}
                    {puedeModificarObjeto("/agro/establecimientos") &&
                      existe && (
                        <Button color="danger" outline onClick={handleEliminar}>
                          <i className="mdi mdi-delete me-1" />
                          Dar de baja
                        </Button>
                      )}
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col lg={5}>
            <Card>
              <CardBody>
                <h6 className="mb-3">Impacto económico anual</h6>
                {!calculo ? (
                  <p className="text-muted mb-0">
                    Guardá el tambo para ver el cálculo.
                  </p>
                ) : (
                  <>
                    <Table size="sm" borderless className="mb-2">
                      <tbody>
                        <tr>
                          <td>Total de cabezas</td>
                          <td className="text-end">
                            {formatearNumero(calculo.totalCabezas, 0)}
                          </td>
                        </tr>
                        <tr>
                          <td>Producción diaria</td>
                          <td className="text-end">
                            {formatearNumero(calculo.litrosDia, 0)} lts
                          </td>
                        </tr>
                        <tr>
                          <td>Producción anual</td>
                          <td className="text-end">
                            {formatearNumero(calculo.litrosAnio, 0)} lts
                          </td>
                        </tr>
                        <tr className="border-top">
                          <td>Ingreso neto</td>
                          <td className="text-end text-success">
                            U$S {formatearNumero(calculo.ingresoAnualUsd, 0)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted small ps-3">
                            Retiro del tambero
                          </td>
                          <td className="text-end text-muted small">
                            −U$S {formatearNumero(calculo.retiroTamberoUsd, 0)}
                          </td>
                        </tr>
                        <tr>
                          <td>Costos</td>
                          <td className="text-end text-danger">
                            U$S {formatearNumero(calculo.costoAnualUsd, 0)}
                          </td>
                        </tr>
                        <tr className="border-top fw-bold">
                          <td>Margen</td>
                          <td
                            className={`text-end ${
                              Number(calculo.margenAnualUsd) < 0
                                ? "text-danger"
                                : "text-success"
                            }`}
                          >
                            U$S {formatearNumero(calculo.margenAnualUsd, 0)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* De dónde salió el litraje: si el tambo no lo tiene
                        cargado, el número viene del parámetro del banco y
                        conviene que se vea. */}
                    <small className="text-muted">
                      <i className="mdi mdi-information-outline me-1" />
                      Calculado con{" "}
                      {formatearNumero(calculo.litrosVacaDiaAplicado, 1)}{" "}
                      lts/vaca/día
                      {!form.litrosVacaDia && " (parámetro del banco)"}.
                      {porDefecto && (
                        <>
                          {" "}
                          Precio del litro U$S {porDefecto.precioLitro}, gasto
                          anual por vaca U$S {porDefecto.gastoAnualVaca},
                          vaquillona de reposición U$S{" "}
                          {porDefecto.precioVaquillona}. Se administran en
                          Administración &gt; Valores Generales.
                        </>
                      )}
                    </small>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  )
}

export default Tambo
